import { useState, type ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faEllipsis } from '@fortawesome/free-solid-svg-icons';
import type { QuestionListItem } from '@course-authoring/shared-types';
import { ListPagination, type AllowedPageSize } from '../table/ListPagination';
import { QUESTION_BANK_BROWSE_COPY as COPY } from './questionBankBrowseCopy';
import { formatAddedDate } from './QuestionBankDetail';
import type { QuestionBankBrowseStyles } from './questionBankBrowseStyles';

/** The card shows at most this many of the question's concepts (Sara's writeup, 2026-11-03); the detail lists them all. */
const CARD_CONCEPTS = 3;

export interface QuestionBankCardsProps {
  styles: QuestionBankBrowseStyles;
  status: 'pending' | 'error' | 'success';
  items: readonly QuestionListItem[];
  /** "Recently added" and "Most used in lessons" add their value to each card. */
  sortKey: string;
  /** The previous page while the next one loads: shown, not activatable. */
  stale: boolean;
  /** The page shows each card's ellipsis only on hover and keyboard focus; the picker always shows it. */
  glyph: 'always' | 'hover';
  total: number;
  page: number;
  pageSize: AllowedPageSize;
  onOpen: (item: QuestionListItem) => void;
  onRetry: () => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: AllowedPageSize) => void;
  /** Rendered at the right of the pagination row, and alone under the empty state or the load error (the page's "Manage questions"). */
  foot?: ReactNode;
  /** Questions already selected (the picker's multi mode): a check in place of the glyph, "Selected" for assistive tech. */
  selectedIds?: ReadonlySet<number>;
}

/**
 * The bank's results: loading, error with Retry, the empty state, or one
 * card per question (text, then one line: its concepts, comma-separated, up
 * to three; "N responses" with N compact; the sort's value; "Q<id>" last;
 * middots between the fields; an ellipsis on hover and focus) followed by
 * the shared list pagination. The concepts are not links here: the whole
 * card opens the detail, where they are.
 */
export function QuestionBankCards({
  styles,
  status,
  items,
  sortKey,
  stale,
  glyph,
  total,
  page,
  pageSize,
  onOpen,
  onRetry,
  onPageChange,
  onPageSizeChange,
  foot,
  selectedIds,
}: QuestionBankCardsProps) {
  const b = styles.browse;
  const c = styles.centerstage;
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [focusedId, setFocusedId] = useState<number | null>(null);

  const sortNote = (item: QuestionListItem): string | null => {
    if (sortKey === 'createdAt') return COPY.added(formatAddedDate(item.createdAt));
    if (sortKey === 'usedIn') return COPY.usedIn(item.usageCount);
    return null;
  };

  const footRow = foot ? <div style={b.listFoot}>{foot}</div> : null;
  if (status === 'pending') return <div role="status" style={c.loadingPanel}>{COPY.loading}</div>;
  if (status === 'error') {
    return (
      <>
        <div role="alert" style={c.errorPanel}>
          <p style={c.emptyBody}>{COPY.loadError}</p>
          <button type="button" style={c.secondaryButton} onClick={onRetry}>{COPY.retry}</button>
        </div>
        {footRow}
      </>
    );
  }
  if (items.length === 0) {
    return (
      <>
        <div style={c.emptyPanel}>
          <p style={c.emptyTitle}>{COPY.emptyTitle}</p>
          <p style={c.emptyBody}>{COPY.emptyBody}</p>
        </div>
        {footRow}
      </>
    );
  }
  const pagination = (
    <ListPagination
      page={page}
      pageSize={pageSize}
      totalCount={total}
      isPlaceholderData={stale}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
    />
  );
  return (
    <>
      <ul style={b.list} data-testid="question-bank-list">
        {items.map((item) => {
          const note = sortNote(item);
          const lifted = hoveredId === item.questionDefinitionId;
          const selected = selectedIds?.has(item.questionDefinitionId) ?? false;
          return (
            <li key={item.questionDefinitionId}>
              <button
                type="button"
                style={{ ...b.card, ...(lifted ? b.cardHover : {}) }}
                onMouseEnter={() => setHoveredId(item.questionDefinitionId)}
                onMouseLeave={() => setHoveredId(null)}
                onFocus={() => setFocusedId(item.questionDefinitionId)}
                onBlur={() => setFocusedId(null)}
                onClick={() => onOpen(item)}
                disabled={stale}
              >
                <span style={b.cardText}>
                  <span style={b.cardQuestion}>{item.questionTextPlain}</span>
                  <span style={b.cardMeta}>
                    {[
                      ...(item.concepts.length > 0 ? [item.concepts.slice(0, CARD_CONCEPTS).map((concept) => concept.name).join(', ')] : []),
                      COPY.responsesCompact(item.responseCount),
                      ...(note ? [note] : []),
                      `Q${item.questionDefinitionId}`,
                    ].join(' · ')}
                  </span>
                </span>
                <span
                  aria-hidden="true"
                  data-testid="card-glyph"
                  style={{
                    ...b.cardGlyph,
                    ...(selected ? b.cardGlyphSelected : {}),
                    ...(glyph === 'hover' && !selected && !lifted && focusedId !== item.questionDefinitionId ? { opacity: 0 } : {}),
                  }}
                >
                  <FontAwesomeIcon icon={selected ? faCheck : faEllipsis} />
                </span>
                {selected ? <span style={b.srOnly}>{COPY.selectedMark}</span> : null}
              </button>
            </li>
          );
        })}
      </ul>
      {foot ? (
        <div style={b.listFoot}>
          <div style={b.listFootPagination}>{pagination}</div>
          {foot}
        </div>
      ) : pagination}
    </>
  );
}
